import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Map<string, { promise: Promise<string | null>, controller: AbortController }>} */
  #sectionPrefetch = new Map();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #prefetchTimer;

  /** @type {string | undefined} */
  #lastFetchedUrl;

  /**
   * Captured at click/change time — whether this option should morph/scroll the gallery.
   * Size = false; Shape/Colour = true. Read later when dispatching variant:update.
   * @type {boolean}
   */
  #pendingUpdatesGallery = true;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.querySelectorAll('.variant-option__button-label').forEach((label) => {
      label.addEventListener('mouseover', this.optionLabelHovered.bind(this));
      label.addEventListener('mouseout', this.optionLabelMouseOut.bind(this));
      label.addEventListener('pointerdown', this.optionLabelPointerDown.bind(this));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.variantChanged.bind(this));
    clearTimeout(this.#prefetchTimer);
    for (const entry of this.#sectionPrefetch.values()) {
      entry.controller.abort();
    }
    this.#sectionPrefetch.clear();
    this.querySelectorAll('.variant-option__button-label').forEach((label) => {
      label.removeEventListener('mouseover', this.optionLabelHovered.bind(this));
      label.removeEventListener('mouseout', this.optionLabelMouseOut.bind(this));
      label.removeEventListener('pointerdown', this.optionLabelPointerDown.bind(this));
    });
  }

  /**
   * Handles the option label hover event.
   * @param {Event} event - The option label hover event.
   */
  optionLabelHovered(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const optionLabel = event.target.closest('.variant-option__button-label');
    if (!(optionLabel instanceof HTMLElement)) return;
    const optionLabelValue = optionLabel.dataset.optionLabelValue;

    const variantOptionButtons = optionLabel.closest('.variant-option--buttons');
    if (!(variantOptionButtons instanceof HTMLElement)) return;
    variantOptionButtons.classList.add('variant-option--buttons-hovered');
    const labelValue = variantOptionButtons.querySelector('.variant-option__label-value');
    if (labelValue instanceof HTMLElement) {
      labelValue.textContent = optionLabelValue || null;
    }

    const input = this.#getInputFromLabel(optionLabel);
    const stockStatus = variantOptionButtons.querySelector('.variant-option__label-stock-status');
    if (stockStatus instanceof HTMLElement && input instanceof HTMLInputElement) {
      stockStatus.hidden = input.dataset.optionAvailable !== 'false';
    }

    if (input && !input.checked) {
      this.#scheduleSectionPrefetch(input);
    }
  }

  /**
   * Starts section prefetch on touch/click before the change event (mobile has no hover).
   * @param {PointerEvent} event
   */
  optionLabelPointerDown(event) {
    if (event.button !== 0) return;
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const input = this.#getInputFromLabel(event.currentTarget);
    if (input && !input.checked) {
      this.#scheduleSectionPrefetch(input, 0);
    }
  }

  /**
   * Handles the option label mouse out event.
   * @param {Event} event - The option label mouse out event.
   */
  optionLabelMouseOut(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const optionLabel = event.target.closest('.variant-option__button-label');
    if (!(optionLabel instanceof HTMLElement)) return;

    const variantOptionButtons = optionLabel.closest('.variant-option--buttons');
    if (!(variantOptionButtons instanceof HTMLElement)) return;
    variantOptionButtons.classList.remove('variant-option--buttons-hovered');
    const labelValue = variantOptionButtons.querySelector('.variant-option__label-value');
    if (labelValue instanceof HTMLElement) {
      labelValue.textContent = labelValue.dataset.optionLabelValue || null;
    }

    const selectedInput = variantOptionButtons.querySelector('input[type="radio"]:checked');
    const stockStatus = variantOptionButtons.querySelector('.variant-option__label-stock-status');
    if (stockStatus instanceof HTMLElement && selectedInput instanceof HTMLInputElement) {
      stockStatus.hidden = selectedInput.dataset.optionAvailable !== 'false';
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // Capture BEFORE fetch — Size only skips scroll-to-gallery (TWS-984); morph/videos match other options.
    this.#pendingUpdatesGallery = this.#optionUpdatesGallery(selectedOption);

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const sectionScopedFetch = isOnProductPage && !loadsNewProduct;

    const requestUrl = this.buildRequestUrl(selectedOption, null, [], sectionScopedFetch);
    this.#lastFetchedUrl = requestUrl;
    this.#consumeSectionPrefetch(requestUrl, loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Whether the page should scroll to the media gallery after this option change.
   * Size (and Ring Size, etc.) return false — TWS-984. Gallery update path is identical either way.
   *
   * @param {Element} selectedOption
   * @returns {boolean}
   */
  #optionUpdatesGallery(selectedOption) {
    const group = selectedOption.closest(
      '[data-updates-gallery], fieldset.variant-option, .variant-option--dropdowns'
    );

    if (!(group instanceof Element)) return true;

    if (group.getAttribute('data-updates-gallery') === 'false') return false;
    if (group.getAttribute('data-updates-gallery') === 'true') return true;

    const optionName = (group.getAttribute('data-option-name') || '').toLowerCase();
    if (optionName === 'size' || optionName.endsWith('-size')) return false;

    const labelText = (group.querySelector('.variant-option__label')?.textContent || '').toLowerCase();
    if (/selected\s+size\s*:/.test(labelText)) return false;

    return true;
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear animation/selection attrs on every radio in this fieldset first.
          radios.forEach((radio) => {
            radio.dataset.previousChecked = 'false';
            radio.dataset.currentChecked = 'false';
          });

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {boolean} [sectionScoped] - When true on PDP, fetch only the parent section (faster than full page).
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], sectionScoped = false) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // Pin Liquid `selected_or_first_available_variant` to the clicked swatch.
    // option_values alone can leave a custom section on the product's first variant,
    // so the fetch overwrites the optimistic swatch price with the original price.
    const variantId = selectedOption.dataset?.variantId;
    if (variantId) {
      params.push(`variant=${encodeURIComponent(variantId)}`);
    }

    // If variant-picker is a child of quick-add or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      if (this.closest('.cart-drawer') || this.closest('.product-card-hover')) {
        return `${productUrl}?section_id=section-rendering-product-card-hover&${params.join('&')}`;

      }
      if (this.closest('.cx-product-slider')) {
        return `${productUrl}?section_id=section-rendering-cx-product-card&${params.join('&')}`;
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    if (sectionScoped && this.dataset.sectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      const sectionQuery = `section_id=${encodeURIComponent(this.dataset.sectionId)}`;
      return params.length ? `${productUrl}?${sectionQuery}&${params.join('&')}` : `${productUrl}?${sectionQuery}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    /** @type {RequestInit} */
    const fetchInit = { signal: this.#abortController.signal };
    if ('priority' in Request.prototype) {
      fetchInit.priority = 'high';
    }

    fetch(requestUrl, fetchInit)
      .then((response) => response.text())
      .then((responseText) => this.#processSectionResponse(responseText, shouldMorphMain))
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Uses a prefetched section response when available; otherwise fetches.
   * @param {string} requestUrl
   * @param {boolean} shouldMorphMain
   */
  #consumeSectionPrefetch(requestUrl, shouldMorphMain) {
    const prefetched = this.#sectionPrefetch.get(requestUrl);

    for (const [url, entry] of this.#sectionPrefetch) {
      if (url !== requestUrl) {
        entry.controller.abort();
        this.#sectionPrefetch.delete(url);
      }
    }

    if (prefetched) {
      this.#sectionPrefetch.delete(requestUrl);
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      prefetched.promise
        .then((responseText) => {
          if (responseText) {
            this.#processSectionResponse(responseText, shouldMorphMain);
          } else {
            this.fetchUpdatedSection(requestUrl, shouldMorphMain);
          }
        })
        .catch(() => {
          this.fetchUpdatedSection(requestUrl, shouldMorphMain);
        });
      return;
    }

    this.fetchUpdatedSection(requestUrl, shouldMorphMain);
  }

  /**
   * @param {string} responseText
   * @param {boolean} shouldMorphMain
   */
  #processSectionResponse(responseText, shouldMorphMain) {
    this.#pendingRequestUrl = undefined;
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    // Defer is only useful for the initial rendering of the page. Remove it here.
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const textContent = html.querySelector(`${this.tagName.toLowerCase()} script[type="application/json"]`)?.textContent;
    if (!textContent) return;

    if (shouldMorphMain) {
      this.updateMain(html);
    } else {
      const newProduct = this.updateVariantPicker(html);

      // We grab the variant object from the response and dispatch an event with it.
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            updatesGallery: this.#pendingUpdatesGallery,
          })
        );
      }
    }
  }

  /**
   * @param {HTMLElement} label
   * @returns {HTMLInputElement | null}
   */
  #getInputFromLabel(label) {
    const input = label.querySelector('input[type="radio"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {HTMLInputElement} hoveredInput
   * @returns {string[]}
   */
  #getOptionValuesForInput(hoveredInput) {
    const fieldsetIndex = Number.parseInt(hoveredInput.dataset.fieldsetIndex || '', 10);
    const groups = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));

    return groups
      .map((group, index) => {
        if (!Number.isNaN(fieldsetIndex) && index === fieldsetIndex) {
          return hoveredInput.dataset.optionValueId ?? null;
        }

        const checkedRadio = group.querySelector('input[type="radio"]:checked');
        if (checkedRadio instanceof HTMLInputElement) {
          return checkedRadio.dataset.optionValueId ?? null;
        }

        const selectedOption = group.querySelector('select option[selected]');
        if (selectedOption instanceof HTMLOptionElement) {
          return selectedOption.dataset.optionValueId ?? null;
        }

        return null;
      })
      .filter((value) => typeof value === 'string');
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string | null}
   */
  #buildPrefetchUrl(input) {
    if (!this.#canSectionPrefetch()) return null;

    let productUrl = input.dataset.connectedProductUrl || this.dataset.productUrl;
    if (!productUrl) return null;

    if (productUrl.includes('?')) {
      productUrl = productUrl.split('?')[0];
    }

    const optionValues = this.#getOptionValuesForInput(input);
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);
    if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);

    const sectionQuery = `section_id=${encodeURIComponent(this.dataset.sectionId ?? '')}`;
    return params.length ? `${productUrl}?${sectionQuery}&${params.join('&')}` : `${productUrl}?${sectionQuery}`;
  }

  /**
   * @returns {boolean}
   */
  #canSectionPrefetch() {
    return (
      this.dataset.templateProductMatch === 'true' &&
      Boolean(this.dataset.sectionId) &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog')
    );
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} [delayMs=60]
   */
  #scheduleSectionPrefetch(input, delayMs = 60) {
    if (!this.#canSectionPrefetch()) return;

    clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = setTimeout(() => {
      const url = this.#buildPrefetchUrl(input);
      if (!url || this.#sectionPrefetch.has(url) || url === this.#lastFetchedUrl) return;

      const controller = new AbortController();
      /** @type {RequestInit} */
      const fetchInit = { signal: controller.signal };
      if ('priority' in Request.prototype) {
        fetchInit.priority = 'low';
      }

      const promise = fetch(url, fetchInit)
        .then((response) => (response.ok ? response.text() : null))
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.warn(error);
          }
          return null;
        });

      this.#sectionPrefetch.set(url, { promise, controller });

      while (this.#sectionPrefetch.size > 8) {
        const oldestKey = this.#sectionPrefetch.keys().next().value;
        if (typeof oldestKey !== 'string') break;
        const oldest = this.#sectionPrefetch.get(oldestKey);
        oldest?.controller.abort();
        this.#sectionPrefetch.delete(oldestKey);
      }
    }, delayMs);
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Reads which option value is selected in each fieldset from a server-rendered picker.
   * @param {ParentNode} root
   * @returns {(string | null)[]}
   */
  #getCheckedOptionValueIdsFrom(root) {
    return Array.from(root.querySelectorAll('fieldset, .variant-option--dropdowns')).map((group) => {
      const checkedRadio = group.querySelector(
        'input[type="radio"][checked], input[type="radio"][data-current-checked="true"]'
      );
      if (checkedRadio instanceof HTMLInputElement) {
        return checkedRadio.dataset.optionValueId ?? null;
      }

      const selectedOption = group.querySelector('select option[selected]');
      if (selectedOption instanceof HTMLOptionElement) {
        return selectedOption.dataset.optionValueId ?? null;
      }

      return null;
    });
  }

  /**
   * @param {string | number} value
   * @returns {string}
   */
  #escapeOptionValueId(value) {
    return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(String(value))
      : String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /**
   * Applies server selection to the live picker after morph (single checked option per fieldset).
   * @param {(string | null)[]} checkedOptionValueIds
   */
  #applyCheckedOptionValueIds(checkedOptionValueIds) {
    const optionGroups = this.querySelectorAll('fieldset, .variant-option--dropdowns');

    optionGroups.forEach((group, index) => {
      group.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = false;
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      });

      const targetId = checkedOptionValueIds[index];
      if (!targetId) return;

      const targetRadio = group.querySelector(
        `input[type="radio"][data-option-value-id="${this.#escapeOptionValueId(targetId)}"]`
      );
      if (targetRadio instanceof HTMLInputElement) {
        targetRadio.checked = true;
        targetRadio.dataset.currentChecked = 'true';

        const labelValue = group.querySelector('.variant-option__label-value');
        if (labelValue instanceof HTMLElement) {
          const displayValue = targetRadio.value || targetRadio.getAttribute('aria-label') || '';
          labelValue.textContent = displayValue;
          labelValue.dataset.optionLabelValue = displayValue;
        }
        return;
      }

      const select = group.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        const targetOption = select.querySelector(
          `option[data-option-value-id="${this.#escapeOptionValueId(targetId)}"]`
        );
        if (targetOption instanceof HTMLOptionElement) {
          select.value = targetOption.value;
          select.querySelectorAll('option').forEach((option) => option.removeAttribute('selected'));
          targetOption.setAttribute('selected', 'selected');
        }
      }
    });

    this.#rebuildRadioRefs();
  }

  /** Rebuild cached radio refs after morph changes option lists (e.g. filtered sizes). */
  #rebuildRadioRefs() {
    this.#radios = [];
    this.#checkedIndices = [];

    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      this.#radios.push(radios);
      const checkedIndex = radios.findIndex((radio) => radio.checked);
      if (checkedIndex !== -1) {
        this.#checkedIndices.push([checkedIndex]);
      }
    });
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // Section-rendered variant responses do not retain collection page context. Preserve the
    // bounded PLP swatch list and its modal trigger before morphing in the response markup.
    const currentPreviewList = this.querySelector('overflow-list[data-plp-modal-preview]');
    const nextPreviewList = newVariantPickerSource.querySelector('overflow-list');

    if (currentPreviewList instanceof HTMLElement && nextPreviewList instanceof HTMLElement) {
      const nextMoreItem = Array.from(nextPreviewList.children).find((child) => child.slot === 'more');
      const currentMoreItem = Array.from(currentPreviewList.children).find((child) => child.slot === 'more');
      const nextSwatchItems = Array.from(nextPreviewList.children).filter(
        (child) => child instanceof HTMLLIElement && child.slot !== 'more'
      );
      const totalItems = Number.parseInt(nextPreviewList.dataset.totalItems || '', 10) || nextSwatchItems.length;

      for (const swatchItem of nextSwatchItems.slice(6)) swatchItem.remove();

      nextPreviewList.setAttribute('data-plp-modal-preview', '');
      nextPreviewList.dataset.totalItems = String(totalItems);
      nextPreviewList.dataset.productUrl =
        newVariantPickerSource.dataset.productUrl || currentPreviewList.dataset.productUrl || '';

      if (nextMoreItem && currentMoreItem) {
        nextMoreItem.replaceWith(currentMoreItem.cloneNode(true));
      }
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    const checkedOptionValueIds = this.#getCheckedOptionValueIdsFrom(newVariantPickerSource);

    morph(this, newVariantPickerSource);

    this.#applyCheckedOptionValueIds(checkedOptionValueIds);
    queueMicrotask(() => this.updatedCallback());

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
